'use client';

import { useCallback, useEffect, useState } from 'react';

import { getFirestoreClient } from '@/lib/firebase/client';
import { getPublicAuthorBySlug, listPublicAuthors } from '@/lib/platform/public-authors';
import type { AuthorProfileDoc } from '@/types/platform';

export function usePublicAuthors() {
  const [authors, setAuthors] = useState<AuthorProfileDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const db = getFirestoreClient();
    if (!db) {
      setAuthors([]);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await listPublicAuthors(db);
      setAuthors(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load public authors.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { authors, loading, error, refresh };
}

export function usePublicAuthor(authorSlug: string) {
  const [author, setAuthor] = useState<AuthorProfileDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const db = getFirestoreClient();
    if (!db) {
      setAuthor(null);
      setLoading(false);
      setError('Firebase client environment is not configured yet.');
      return;
    }
    try {
      setLoading(true);
      const data = await getPublicAuthorBySlug(db, authorSlug);
      setAuthor(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load author profile.');
    } finally {
      setLoading(false);
    }
  }, [authorSlug]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { author, loading, error, refresh };
}
